import { Box, BoxProps, Tooltip, Typography, useTheme } from '@mui/material';
import { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Check, Clock, X } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';

export default function ReviewStatus({
    status,
    fontSize = 14,
    fontWeight = 400,
    boxProps,
    color,
    onClick,
    tooltip,
    dataTestId,
}: {
    status: DICOMApprovalStatus;
    fontSize?: number;
    fontWeight?: number;
    boxProps?: BoxProps;
    color?: string;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
    tooltip?: ReactNode;
    dataTestId?: string;
}) {
    const { palette } = useTheme();

    const getStatusConfig = () => {
        switch (status) {
            case DICOMApprovalStatus.APPROVED:
                return {
                    icon: (
                        <Check
                            size={fontSize - 1}
                            color={palette.success.main}
                        />
                    ),
                    labelId: 'approved',
                };
            case DICOMApprovalStatus.REJECTED:
                return {
                    icon: <X size={fontSize - 1} color={palette.error.main} />,
                    labelId: 'rejected',
                };
            default:
                return {
                    icon: (
                        <Clock
                            size={fontSize - 3}
                            color={palette.warning.main}
                            style={{ marginRight: 2 }}
                        />
                    ),
                    labelId: 'unapproved',
                };
        }
    };

    const { icon, labelId } = getStatusConfig();

    const isClickable = Boolean(onClick);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
        }
    };

    const { sx: boxSx, ...restBoxProps } = boxProps ?? {};

    const content = (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            onClick={onClick}
            onKeyDown={isClickable ? handleKeyDown : undefined}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            data-testid={dataTestId}
            {...restBoxProps}
            sx={[
                isClickable && {
                    cursor: 'pointer',
                    borderRadius: 0.5,
                    px: 0.5,
                    mx: -0.5,
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${palette.primary.main}`,
                        outlineOffset: 1,
                    },
                },
                ...(Array.isArray(boxSx) ? boxSx : boxSx ? [boxSx] : []),
            ]}
        >
            {icon}
            <Typography
                fontSize={fontSize}
                fontWeight={fontWeight}
                lineHeight={1.5}
                color={color}
            >
                <FormattedMessage id={labelId} />
            </Typography>
        </Box>
    );

    if (tooltip) {
        return <Tooltip title={tooltip}>{content}</Tooltip>;
    }

    return content;
}
